import { MessageSchema } from "@redotech/redo-agents-common/llm-messages";
import { PageContextSchema } from "@redotech/redo-agents-common/page-context";
import { unaryStreamRpc } from "@redotech/rpc/definition";
import * as z from "zod";
import { MerchantSupportAgentThreadSchema } from "./message-merchant-support-agent";

/**
 * Streaming event schemas for merchant support agent
 *
 * - text-delta: Yields text tokens as they're generated
 * - tool-call: Yields when a tool call is complete (not during generation)
 * - reasoning: Extended thinking content (if available)
 * - complete: Final state with thread IDs and messages
 * - error: Error occurred during processing
 */

const TextDeltaEventSchema_ = z.object({
  type: z.literal("text-delta"),
  textDelta: z.string(),
});
export const TextDeltaEventSchema: z.ZodType<TextDeltaEventSchema> =
  TextDeltaEventSchema_;
export interface TextDeltaEventSchema extends z.infer<
  typeof TextDeltaEventSchema_
> {}

const ToolCallEventSchema_ = z.object({
  type: z.literal("tool-call"),
  toolCall: z.object({
    toolCallId: z.string(),
    toolName: z.string(),
    input: z.unknown(),
  }),
});
export const ToolCallEventSchema: z.ZodType<ToolCallEventSchema> =
  ToolCallEventSchema_;
export interface ToolCallEventSchema extends z.infer<
  typeof ToolCallEventSchema_
> {}

const ReasoningEventSchema_ = z.object({
  type: z.literal("reasoning"),
  text: z.string(),
});
export const ReasoningEventSchema: z.ZodType<ReasoningEventSchema> =
  ReasoningEventSchema_;
export interface ReasoningEventSchema extends z.infer<
  typeof ReasoningEventSchema_
> {}

const StatusEventSchema_ = z.object({
  type: z.literal("status"),
  status: z.string(),
});
export const StatusEventSchema: z.ZodType<StatusEventSchema> =
  StatusEventSchema_;
export interface StatusEventSchema extends z.infer<typeof StatusEventSchema_> {}

const CompleteEventSchema_ = z.object({
  type: z.literal("complete"),
  thread: MerchantSupportAgentThreadSchema,
});
export const CompleteEventSchema: z.ZodType<CompleteEventSchema> =
  CompleteEventSchema_;
export interface CompleteEventSchema extends z.infer<
  typeof CompleteEventSchema_
> {}

const ErrorEventSchema_ = z.object({
  type: z.literal("error"),
  error: z.string(),
});
export const ErrorEventSchema: z.ZodType<ErrorEventSchema> = ErrorEventSchema_;
export interface ErrorEventSchema extends z.infer<typeof ErrorEventSchema_> {}

const StreamMerchantSupportEventSchema_ = z.discriminatedUnion("type", [
  TextDeltaEventSchema_,
  ToolCallEventSchema_,
  ReasoningEventSchema_,
  StatusEventSchema_,
  CompleteEventSchema_,
  ErrorEventSchema_,
]);
export const StreamMerchantSupportEventSchema: z.ZodType<StreamMerchantSupportEvent> =
  StreamMerchantSupportEventSchema_;
export type StreamMerchantSupportEvent = z.infer<
  typeof StreamMerchantSupportEventSchema_
>;

export const streamMerchantSupportAgent = unaryStreamRpc({
  name: "streamMerchantSupportAgent" as const,
  input: z.object({
    messages: z.array(MessageSchema),
    currentThreadId: z.string().optional(),
    triageThreadId: z
      .string()
      .optional()
      .describe("If left undefined, a new thread will be created."),
    userEmail: z
      .string()
      .optional()
      .describe("User email for context when creating a new thread."),
    pageContext: PageContextSchema.optional().describe(
      "Page context object with structured data.",
    ),
    merchantTimezone: z
      .string()
      .optional()
      .describe(
        "The merchant's IANA timezone (e.g., 'America/Denver'). Used for date-aware agent responses.",
      ),
  }),
  output: StreamMerchantSupportEventSchema,
});
